import React, {useState, useEffect, useMemo} from "react";
import {Stack, Button, Card, HStack, Text} from "@chakra-ui/react";
import axios from "../axios/axios";
import moment from "moment";
import TaskTable from "../components/TaskTable";
import formatHoursAndMinutes from "../lib/helpers/formatHoursAndMinutes";
import {useTasks} from "../providers/TasksProvider";
import {Header} from "../components/Header";
import {format} from "date-fns";

const currentDate = moment();
const formattedDate = currentDate.format("YYYY-MM-DD");

export default function TasksContainer({user}) {
    const {inProgressTasks, fetchTasks} = useTasks();
    const [selectedDate] = useState(new Date());
    const [totalHoursMessage, setTotalHoursMessage] = useState("");

    useEffect(() => {
        let timer;
        if (totalHoursMessage) {
            timer = setTimeout(() => {
                setTotalHoursMessage("");
            }, 5000);
        }
        return () => clearTimeout(timer);
    }, [totalHoursMessage]);

    const tasksForSelectedDate = useMemo(
        () =>
            inProgressTasks.filter(task => {
                const taskDate = format(new Date(task.created_at), "yyyy-MM-dd");
                const selectedDateFormatted = format(selectedDate, "yyyy-MM-dd");
                return taskDate === selectedDateFormatted;
            }),
        [inProgressTasks, selectedDate]
    );

    const previousUserTasks = useMemo(
        () =>
            inProgressTasks.filter(task => {
                const taskDate = format(new Date(task.created_at), "yyyy-MM-dd");
                const selectedDateFormatted = format(selectedDate, "yyyy-MM-dd");
                return taskDate !== selectedDateFormatted;
            }),
        [inProgressTasks, selectedDate]
    );

    const uniqueDates = [...new Set(previousUserTasks.map(task => moment(task.created_at).format("YYYY-MM-DD")))].sort((a, b) => moment(a).diff(moment(b)));

    const handleDeleteTask = taskUniqueId => {
        axios
            .delete(`/user_task/${taskUniqueId}`)
            .then(() => {
                fetchTasks();
            })
            .catch(error => console.error("Error deleting the task:", error));
    };

    const onDuplicateTask = taskId => {
        axios
            .post("/duplicate_task", {
                task_id: taskId,
            })
            .then(() => {
                fetchTasks();
            })
            .catch(error => console.error("Error duplicating the task:", error));
    };

    const onEditTask = (taskId, timeSpent) => {
        axios
            .put(`/user_task/${taskId}`, {
                time_spent: timeSpent,
            })
            .then(() => {
                fetchTasks();
            })
            .catch(error => console.error("Error editing the task:", error));
    };

    const totalCumulatedTime = useMemo(() => tasksForSelectedDate.reduce((totalTime, task) => totalTime + parseFloat(task.time_spent), 0) / 60, [tasksForSelectedDate]);
    const remainingTime = 7 - totalCumulatedTime;

    const markTaskAsDone = () => {
        setTotalHoursMessage("");
        // Calculer le total des heures pour la journée en cours
        const totalHoursForToday = tasksForSelectedDate.reduce((total, task) => total + task.time_spent / 60, 0);

        if (totalHoursForToday < 7) {
            setTotalHoursMessage("⚠️ Impossible de terminer la journée. Le total des heures doit être d'au moins 7 heures.");
            return;
        }

        axios
            .post("/task_done", {
                user_id: user.id,
                date: formattedDate,
            })
            .then(() => {
                fetchTasks();
            })
            .catch(error => console.error("Error marking all tasks as completed:", error));
    };

    const handleMarkAsCompleted = async taskId => {
        try {
            await axios.post("/mark_task_completed", {
                task_id: taskId,
            });
            fetchTasks();
        } catch (error) {
            console.error("Erreur lors du marquage de la tâche comme terminée:", error);
        }
    };

    const activeUserTasks = tasksForSelectedDate.filter(task => task.completed === 0);

    return (
        <>
            {uniqueDates
                .filter(date => previousUserTasks.some(task => moment(task.created_at).isSame(date, "day") && !task.completed))
                .map(date => {
                    const tasksForDate = previousUserTasks.filter(task => moment(task.created_at).isSame(date, "day"));
                    const totalTimeForDate = tasksForDate.reduce((total, task) => total + parseFloat(task.time_spent), 0) / 60;

                    return (
                        <Card display={"flex"} justifyContent={"center"} alignItems={"center"} key={date}>
                            <Stack direction={"row"} p={15}>
                                <Header as={"h3"} size={"md"}>
                                    Tâches du {moment(date).format("DD/MM/YYYY")} :
                                </Header>
                            </Stack>
                            {totalTimeForDate < 7 && (
                                <Text color="red.500" mb={2}>
                                    ⚠️ Attention : Seulement {formatHoursAndMinutes(totalTimeForDate)} de comptabilisées
                                    sur ce jour
                                </Text>
                            )}
                            {/* Table des tâches des jours précédents */}
                            <TaskTable
                                userTasks={previousUserTasks.filter(task => moment(task.created_at).isSame(date, "day") && !task.completed)}
                                onMarkAsCompleted={handleMarkAsCompleted}
                                onDeleteTask={handleDeleteTask}
                                onDuplicateTask={onDuplicateTask}
                                onEditTask={onEditTask}
                            />
                        </Card>
                    );
                })}
            <Card backgroundColor={"white"} display={"flex"} alignItems={"center"} justifyContent={"center"}>
                <Stack direction={"row"} p={15}>
                    <Header as={"h3"} size={"md"}>
                        Tâches effectuées :
                    </Header>
                </Stack>
                {totalHoursMessage && <Text color="red.500">{totalHoursMessage}</Text>}
                {/* La table existante pour les tâches effectuées */}
                <TaskTable
                    userTasks={activeUserTasks}
                    onDeleteTask={handleDeleteTask}
                    onMarkAsCompleted={handleMarkAsCompleted}
                    onDuplicateTask={onDuplicateTask}
                    onEditTask={onEditTask}
                />
                <Text my={25}>
                    Temps restant à
                    effectuer: {remainingTime === 0 || remainingTime < 0 ? "Vous avez atteint votre quota horaire pour la journée." : formatHoursAndMinutes(remainingTime)}
                </Text>

                {(remainingTime === 0 || remainingTime < 0) &&
                    <Text>Total de votre journée : {formatHoursAndMinutes(totalCumulatedTime)}.</Text>}
            </Card>
            <HStack display={"flex"} justifyContent={"center"}>
                <Button onClick={markTaskAsDone}>Marquer toutes les tâches comme terminées</Button>
            </HStack>
        </>
    );
}
